import { Modal, Input, Checkbox, App, Table, Space, Typography, Empty, Spin, Tooltip } from 'antd';
import { useState, useMemo, useEffect } from 'react';
import { PRICE_REFERENCE_TIP } from '../../../constants';
import BenefitTags from './BenefitTags';
import './SelectBenefitModal.less';

const { Text } = Typography;

/** 权益列表区域总高度（px） */
const BENEFIT_LIST_HEIGHT = 300;
/** small 表头高度约 40px，body 滚动高度需扣除表头，否则会被外层裁切无法滚动 */
const BENEFIT_TABLE_HEADER_HEIGHT = 40;
const BENEFIT_TABLE_SCROLL_Y = BENEFIT_LIST_HEIGHT - BENEFIT_TABLE_HEADER_HEIGHT;

/**
 * 格式化权益总量展示：0 表示无限制
 * @param {number|null|undefined} quantity 权益总量
 * @returns {import('react').ReactNode}
 */
function renderTotalQuantity(quantity) {
    if (quantity == null) return <Text type="secondary">--</Text>;
    if (quantity === 0) return '无限制';
    return quantity;
}

/**
 * 权益选择弹窗 - 按分类展示会议权益池供用户勾选（数据由父组件拉取并传入）
 * @param {Object} props
 * @param {boolean} props.open 弹窗是否打开
 * @param {number} props.categoryId 当前权益分类 id（用于过滤）
 * @param {string} props.categoryName 当前权益分类名称（用于标题）
 * @param {Array} props.allBenefits 全部会议权益列表
 * @param {boolean} [props.loading] 权益列表加载中
 * @param {number[]} props.currentSelected 当前已选中的 conferenceBenefitId 数组
 * @param {Function} props.onConfirm 确认回调，参数为选中的权益对象数组
 * @param {Function} props.onCancel 取消回调
 */
export default function SelectBenefitModal({ open, categoryId, categoryName, allBenefits, loading = false, currentSelected = [], onConfirm, onCancel }) {
    const { modal } = App.useApp();
    const [checkedIds, setCheckedIds] = useState([]);
    const [searchKeyword, setSearchKeyword] = useState('');

    useEffect(() => {
        if (open) {
            setCheckedIds([...currentSelected]);
            setSearchKeyword('');
        }
    }, [open, currentSelected]);

    const filteredBenefits = useMemo(() => {
        const categoryItems = allBenefits.filter(b => b.type === categoryId);
        if (!searchKeyword.trim()) return categoryItems;
        return categoryItems.filter(b => b.benefitName.toLowerCase().includes(searchKeyword.trim().toLowerCase()));
    }, [allBenefits, categoryId, searchKeyword]);

    const isDirty = useMemo(() => {
        if (checkedIds.length !== currentSelected.length) return true;
        const sorted1 = [...checkedIds].sort();
        const sorted2 = [...currentSelected].sort();
        return sorted1.some((v, i) => v !== sorted2[i]);
    }, [checkedIds, currentSelected]);

    /**
     * 只允许勾选/取消不在 currentSelected 中的权益；
     * 已选权益勾选框禁用，只能通过外部移除按钮删除。
     */
    const handleToggle = (id, checked) => {
        if (currentSelected.includes(id)) return;
        setCheckedIds(prev => (checked ? [...prev, id] : prev.filter(v => v !== id)));
    };

    /** 当前列表下可勾选的权益 id（排除已选禁用项），全选仅作用于这部分 */
    const selectableIds = useMemo(() => filteredBenefits.filter(b => !currentSelected.includes(b.id)).map(b => b.id), [filteredBenefits, currentSelected]);

    const checkedSelectableCount = useMemo(() => selectableIds.reduce((count, id) => (checkedIds.includes(id) ? count + 1 : count), 0), [selectableIds, checkedIds]);

    const isAllSelectableChecked = selectableIds.length > 0 && checkedSelectableCount === selectableIds.length;
    const isSomeSelectableChecked = checkedSelectableCount > 0 && !isAllSelectableChecked;

    /**
     * 表头全选：勾选/取消当前列表下可勾选的权益，已选禁用项保持不变
     */
    const handleSelectAll = checked => {
        setCheckedIds(prev => (checked ? [...new Set([...prev, ...selectableIds])] : prev.filter(id => !selectableIds.includes(id))));
    };

    const handleCancel = () => {
        if (isDirty) {
            modal.confirm({
                title: '提示',
                content: '发现有数据变动，确认不保存并关闭？',
                okText: '确认',
                cancelText: '取消',
                centered: true,
                onOk: () => onCancel(),
            });
        } else {
            onCancel();
        }
    };

    const handleSave = () => {
        const selected = allBenefits.filter(b => checkedIds.includes(b.id));
        onConfirm(selected);
    };

    const columns = [
        {
            title: <Checkbox checked={isAllSelectableChecked} indeterminate={isSomeSelectableChecked} disabled={selectableIds.length === 0} onChange={e => handleSelectAll(e.target.checked)} />,
            dataIndex: 'id',
            width: 50,
            render: id => {
                const isExisting = currentSelected.includes(id);
                return <Checkbox checked={checkedIds.includes(id)} disabled={isExisting} onChange={e => handleToggle(id, e.target.checked)} />;
            },
        },
        {
            title: '序号',
            width: 60,
            render: (_, _record, index) => index + 1,
        },
        {
            title: '权益名称',
            dataIndex: 'benefitName',
            render: (name, record) => (
                <div>
                    <Space>
                        <span>{name}</span>
                        <BenefitTags isBoothBenefit={record.isBoothBenefit} builtin={record.builtin} />
                    </Space>
                    <div className="select-benefit-modal-benefit-desc">
                        <div>权益说明：{record.benefitDesc || '-'}</div>
                        <div>
                            价格参照
                            {record.builtin !== 0 && (
                                <Tooltip title={PRICE_REFERENCE_TIP}>
                                    <i className="bi bi-question-circle-fill" />
                                </Tooltip>
                            )}
                            ：{record.priceReference || '-'}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            title: '权益总量',
            dataIndex: 'quantity',
            width: 100,
            render: renderTotalQuantity,
        },
        {
            title: '已售数量',
            dataIndex: 'soldCount',
            width: 100,
            render: val => (val != null ? val : <Text type="secondary">--</Text>),
        },
    ];

    return (
        <Modal title="选择权益" open={open} width={680} centered onCancel={handleCancel} onOk={handleSave} okText="保存" cancelText="取消" destroyOnHidden>
            <div className="select-benefit-modal">
                <div className="select-benefit-modal-tip">若未找到“商务函”中权益，请联系权益管理负责人，在商务权益管理维护后再次选择。</div>
                <Input className="select-benefit-modal-search" placeholder="输入权益名称关键字" allowClear prefix={<i className="bi bi-search" />} value={searchKeyword} onChange={e => setSearchKeyword(e.target.value)} />
                <div className="select-benefit-modal-category">
                    {categoryName}（{checkedIds.length}项）
                </div>
                <div className="select-benefit-modal-list" style={{ '--select-benefit-list-height': `${BENEFIT_LIST_HEIGHT}px` }}>
                    <Table
                        rowKey="id"
                        columns={columns}
                        dataSource={filteredBenefits}
                        loading={loading}
                        pagination={false}
                        size="small"
                        scroll={{ y: BENEFIT_TABLE_SCROLL_Y }}
                        locale={{
                            emptyText: loading ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
                                    <Spin size="small" />
                                </div>
                            ) : (
                                <Empty description="未添加权益" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                            ),
                        }}
                    />
                </div>
            </div>
        </Modal>
    );
}
